import React, { useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Form, Modal, Toast, ToastContainer } from "react-bootstrap";
import { getApiErrorMessage } from "../../../store/api/baseApi";
import { useCreateRequestMutation } from "../../../store/api/requestsApi";
import "./SubmitRequestPopup.css";

interface RequestFormData {
  name: string;
  phone: string;
  email: string;
  projectType: string;
  message: string;
}

const ScheduleMeeting: React.FC = () => {
  const [createRequest, { isLoading }] = useCreateRequestMutation();
  const [show, setShow] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validated, setValidated] = useState(false);
  const [formData, setFormData] = useState<RequestFormData>({
    name: "",
    phone: "",
    email: "",
    projectType: "",
    message: "",
  });

  const handleOpen = () => setShow(true);
  const handleClose = () => {
    setShow(false);
    setValidated(false);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);
    try {
      await createRequest(formData).unwrap();
      setErrorMessage("");
      setShowSuccessToast(true);
      setFormData({
        name: "",
        phone: "",
        email: "",
        projectType: "",
        message: "",
      });
      setValidated(false);
      handleClose();
    } catch (apiError) {
      setErrorMessage(getApiErrorMessage(apiError as FetchBaseQueryError));
      setShowErrorToast(true);
    }
  };

  return (
    <>
      <button className='btn schedule-meeting-btn' onClick={handleOpen}>
        Schedule Meeting
      </button>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        scrollable
        backdrop={false}
        dialogClassName='request-modal-dialog'
        className='request-modal'
      >
        <Modal.Header
          closeButton
          closeVariant='white'
          className='request-modal-header'
        >
          <Modal.Title id='submitRequestTitle' className='fw-bold'>
            Submit Your Request
          </Modal.Title>
        </Modal.Header>

        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Body className='request-modal-body'>
            <p className='mb-3 request-text'>
              Fill in the details below. Our team will contact you within one
              business day.
            </p>

            <div className='row g-3'>
              <div className='col-md-6'>
                <Form.Group controlId='requestFullName'>
                  {/* <Form.Label>Full Name</Form.Label> */}
                  <Form.Control
                    name='name'
                    type='text'
                    placeholder='Enter your full name'
                    minLength={2}
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <Form.Control.Feedback type='invalid'>
                    Please enter your full name.
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group controlId='requestPhone'>
                  {/* <Form.Label>Phone Number</Form.Label> */}
                  <Form.Control
                    name='phone'
                    type='tel'
                    placeholder='Enter your phone number'
                    minLength={10}
                    maxLength={10}
                    pattern='[0-9]{10}'
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                  <Form.Control.Feedback type='invalid'>
                    Enter a valid 10-digit phone number.
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group controlId='requestEmail'>
                  {/* <Form.Label>Email Address</Form.Label> */}
                  <Form.Control
                    name='email'
                    type='email'
                    placeholder='Enter your email address'
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <Form.Control.Feedback type='invalid'>
                    Please enter a valid email address.
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group controlId='requestType'>
                  {/* <Form.Label>Project Type</Form.Label> */}
                  <Form.Select
                    name='projectType'
                    value={formData.projectType}
                    onChange={handleChange}
                    required
                  >
                    <option value=''>Select project type</option>
                    <option value='Residential'>Residential</option>
                    <option value='Commercial'>Commercial</option>
                    <option value='Industrial'>Industrial</option>
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    Please select a project type.
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className='col-12'>
                <Form.Group controlId='requestMessage'>
                  {/* <Form.Label>Project Requirement</Form.Label> */}
                  <Form.Control
                    as='textarea'
                    name='message'
                    placeholder='Tell us about your project requirement'
                    minLength={10}
                    value={formData.message}
                    onChange={handleChange}
                    style={{ height: "120px" }}
                    required
                  />
                  <Form.Control.Feedback type='invalid'>
                    Please enter at least 10 characters for the requirement.
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <p className='request-note mt-3 mb-0'>
              All fields are required. We only use this information to contact
              you regarding your request.
            </p>
          </Modal.Body>

          <Modal.Footer className='request-modal-footer'>
            <Button
              type='button'
              variant='outline-secondary'
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              className='brand-btn-primary border-0'
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit Request"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ToastContainer position='bottom-end' className='p-3'>
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={3000}
          autohide
          bg='success'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Request Sent</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            Success! Your request has been submitted.
          </Toast.Body>
        </Toast>
        <Toast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          delay={4000}
          autohide
          bg='danger'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Request Failed</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            {errorMessage || "Unable to submit request. Please try again."}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default ScheduleMeeting;
